export interface ExcelRangeData {
  values: any[][];
  text: string[][];
  formulas: string[][];
  numberFormat?: any[][];
  address: string;
  // If Graph API or a custom function returns formatting arrays (not standard in basic Graph calls, but requested by prompt)
  formats?: {
    fill?: { color: string }[][];
    font?: { bold: boolean; color: string; italic: boolean }[][];
  };
  mergedAreas?: string[]; // Array of range addresses that are merged, e.g. ["Sheet1!A1:B2"]
  isTable?: boolean; // Indicates if the range is part of a formatted Table
  tableStyle?: string;
}

/**
 * Converts a column letter (e.g. "A", "Z", "AA") to a 0-based column index.
 */
export function colLetterToIndex(letter: string): number {
  let index = 0;
  for (let i = 0; i < letter.length; i++) {
    index = index * 26 + (letter.charCodeAt(i) - 64);
  }
  return index - 1;
}

/**
 * Parses an Excel cell address (e.g., "Sheet1!A1", "A1") into 0-based row and col index.
 */
export function parseCellAddress(address: string): { row: number; col: number } {
  // Strip sheet name if present
  const cellAddress = address.includes('!') ? address.split('!')[1] : address;
  const match = cellAddress.match(/([a-zA-Z]+)(\d+)/);
  if (!match) return { row: 0, col: 0 };
  return {
    col: colLetterToIndex(match[1].toUpperCase()),
    row: parseInt(match[2], 10) - 1
  };
}

/**
 * Parses an Excel range address (e.g. "A1:B2") into 0-based start/end row/col coordinates.
 */
export function parseRangeAddress(address: string) {
  // Strip sheet name
  const cleanAddress = address.includes('!') ? address.split('!')[1] : address;
  if (!cleanAddress.includes(':')) {
    const cell = parseCellAddress(cleanAddress);
    return { startRow: cell.row, endRow: cell.row, startCol: cell.col, endCol: cell.col };
  }
  const parts = cleanAddress.split(':');
  const start = parseCellAddress(parts[0]);
  const end = parseCellAddress(parts[1]);
  return {
    startRow: Math.min(start.row, end.row),
    endRow: Math.max(start.row, end.row),
    startCol: Math.min(start.col, end.col),
    endCol: Math.max(start.col, end.col),
  };
}

/**
 * Maps Excel styling (from Graph API or custom object) to CSS properties
 */
export function mapExcelStylesToCSS(
  rowIndex: number,
  colIndex: number,
  rangeData: ExcelRangeData
): React.CSSProperties {
  const styles: React.CSSProperties = {
    padding: '4px 8px',
    border: '1px solid #e0e0e0',
    minWidth: '60px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '12px',
    fontFamily: 'Calibri, sans-serif'
  };

  if (rangeData.formats) {
    // Fill color
    if (rangeData.formats.fill && rangeData.formats.fill[rowIndex] && rangeData.formats.fill[rowIndex][colIndex]) {
      const fill = rangeData.formats.fill[rowIndex][colIndex];
      if (fill.color && fill.color !== 'none') {
        styles.backgroundColor = fill.color;
      }
    }

    // Font styles
    if (rangeData.formats.font && rangeData.formats.font[rowIndex] && rangeData.formats.font[rowIndex][colIndex]) {
      const font = rangeData.formats.font[rowIndex][colIndex];
      if (font.bold) styles.fontWeight = 'bold';
      if (font.italic) styles.fontStyle = 'italic';
      if (font.color) styles.color = font.color;
    }
  }

  return styles;
}

/**
 * Fetches range data from Microsoft Graph API.
 * Note: Retrieving per-cell formatting requires either specialized batching or specific expand options in Graph.
 * We fetch the standard 2D arrays (values, text, formulas) and simulate formatting response structure.
 */
export async function fetchExcelRange(
  graphToken: string,
  driveItemId: string,
  worksheetName: string,
  rangeAddress: string
): Promise<ExcelRangeData> {
  // If no specific range is provided, fallback to the usedRange of the entire sheet
  const endpoint = (!rangeAddress || rangeAddress.trim() === 'auto' || rangeAddress.trim() === '') 
    ? `usedRange` 
    : `range(address='${rangeAddress}')`;

  // Use /drives/{driveId}/items/{id} if driveItemId contains a colon (cross-tenant format)
  // otherwise fall back to the /me/drive/items path for own-drive files.
  const itemPath = driveItemId.includes('!')
    ? `drives/${driveItemId.split('!')[0]}/items/${driveItemId}`
    : `me/drive/items/${driveItemId}`;
  const url = `https://graph.microsoft.com/v1.0/${itemPath}/workbook/worksheets('${worksheetName}')/${endpoint}`;

  // Plain, sessionless GET — the proven path. (An earlier attempt to defeat the
  // post-edit propagation lag with a workbook session + forced recalc was reverted:
  // it didn't fix the lag and made createSession the first call, which the Excel
  // backend can reject for opaque personal-account tokens.) `no-store` avoids any
  // local HTTP cache; OneDrive propagation lag is handled by the caller's poll.
  const response = await fetch(url, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Authorization': `Bearer ${graphToken}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    // Capture status + Graph's machine-readable error code so token/permission
    // failures are unambiguous (e.g. 401 InvalidAuthenticationToken vs 404).
    const bodyText = await response.text().catch(() => "");
    let code = "", message = response.statusText;
    try {
      const parsed = JSON.parse(bodyText);
      code = parsed?.error?.code || "";
      message = parsed?.error?.message || message;
    } catch { /* non-JSON body */ }
    throw new Error(`Graph API Error (${response.status}${code ? " " + code : ""}): ${message}`);
  }

  const data = await response.json();

  // If it's a table, we can query its styling/details. We'll check if the address represents a table here in a real app.
  const isTable = !!data.table; 
  
  // Note: For true full-fidelity formatting, Graph API requires complex formatting queries.
  // We map the base response to our ExcelRangeData interface.
  return {
    values: data.values,
    text: data.text,
    formulas: data.formulas,
    numberFormat: data.numberFormat,
    address: data.address,
    // Provide an empty/mock formats object so the renderer doesn't crash.
    formats: {
      fill: [],
      font: []
    },
    // We would parse data.mergedAreas if the Graph API expansion supported it.
    // Simulating empty or using standard graph response if available:
    mergedAreas: data.mergedAreas || [],
    isTable,
    tableStyle: data.tableStyle
  };
}

/**
 * Encodes a sharing URL into the format required by Microsoft Graph /shares endpoint.
 */
function encodeSharingUrl(url: string): string {
  const bytes = new TextEncoder().encode(url);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const b64 = btoa(binary);
  return "u!" + b64.replace(/=/g, '').replace(/\//g, '_').replace(/\+/g, '-');
}

/**
 * Attempts to resolve a given OneDrive/SharePoint URL into a DriveItem ID.
 *
 * Cross-account / cross-tenant behaviour:
 *  - Authenticated request first (fastest for files in the user's own OneDrive).
 *  - If Graph returns 308 (permanent redirect for cross-tenant files) the browser
 *    cannot follow it due to CORS stripping the Authorization header on redirect.
 *    We retry WITHOUT the auth header — the /shares endpoint supports anonymous
 *    access for "Anyone with the link" sharing links.
 */
export async function resolveDriveItemFromUrl(graphToken: string, sharingUrl: string): Promise<string> {
  const encodedUrl = encodeSharingUrl(sharingUrl.trim());
  const endpoint = `https://graph.microsoft.com/v1.0/shares/${encodedUrl}/driveItem`;

  // ── Attempt 1: authenticated (own OneDrive / same-tenant files) ─────────────
  const authResponse = await fetch(endpoint, {
    headers: { 'Authorization': `Bearer ${graphToken}` },
  });

  if (authResponse.ok) {
    const data = await authResponse.json();
    if (!data.id) throw new Error("DriveItem ID not found for this link.");
    return data.id;
  }

  // ── Attempt 2: anonymous (cross-account "Anyone with the link" files) ────────
  // Graph's /shares endpoint allows unauthenticated access for anonymous links.
  // This avoids the 308 cross-tenant redirect that CORS blocks in the browser.
  if (authResponse.status === 308 || authResponse.status === 307 || authResponse.status === 403) {
    const anonResponse = await fetch(endpoint).catch(() => null);
    if (anonResponse?.ok) {
      const data = await anonResponse.json();
      if (data?.id) return data.id;
    }

    // Both attempts failed — give a specific, actionable error for cross-account links
    throw new Error(
      "This file is hosted on a different Microsoft account. " +
      "For cross-account files, make sure the link is set to " +
      "'Anyone with the link can edit' and try again. " +
      "Alternatively, copy the file into your own OneDrive and link from there."
    );
  }

  // ── All other errors — show a friendly message, never the raw Graph/JWT text ──
  const errorBody = await authResponse.json().catch(() => ({}));
  const raw = (errorBody as any).error?.message || authResponse.statusText || "";
  console.error(`resolveDriveItemFromUrl failed (${authResponse.status}): ${raw}`);

  // Token / permission problems (e.g. IDX14100 right after sign-in, before OneDrive
  // is connected) → guide the user to connect OneDrive, not a wall of JWT jargon.
  if (authResponse.status === 401 || /IDX14100|not well formed|JWT|InvalidAuthenticationToken|CompactToken|expired|Unauthorized/i.test(raw)) {
    throw new Error(
      "MarginPro isn’t connected to your OneDrive yet (or the connection expired). " +
      "Press the Reconnect button, then paste the Excel link again."
    );
  }
  if (authResponse.status === 404) {
    throw new Error("We couldn’t find that file. Double-check the OneDrive/SharePoint sharing link and that you have access to it.");
  }
  throw new Error("We couldn’t open that Excel link. Please make sure it’s a valid OneDrive or SharePoint sharing link that you have access to.");
}

/**
 * Fetches the list of worksheets available in a given DriveItem (Excel file).
 */
export async function getWorksheets(graphToken: string, driveItemId: string): Promise<{ id: string, name: string }[]> {
  const itemPath = driveItemId.includes('!')
    ? `drives/${driveItemId.split('!')[0]}/items/${driveItemId}`
    : `me/drive/items/${driveItemId}`;
  const response = await fetch(`https://graph.microsoft.com/v1.0/${itemPath}/workbook/worksheets`, {
    headers: { 'Authorization': `Bearer ${graphToken}` }
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Your OneDrive connection expired. Press the Reconnect button, then try again.");
    }
    throw new Error("We couldn’t open that workbook. Make sure it’s a valid Excel file you have access to.");
  }
  
  const data = await response.json();
  return data.value || [];
}
